import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Trash2, Plus, Users, Building2, MapPin, Briefcase, History, Mail, Phone } from "lucide-react";

const KIND_ICONS = { project: Briefcase, enquiry: Mail, quote: Mail, contract: Mail };

const AdminClientDetail = () => {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState("overview");
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", designation: "" });
  const [newAddress, setNewAddress] = useState({ label: "Site", line1: "", city: "", country: "IN" });

  const load = useCallback(async () => {
    setLoading(true);
    // Retry once on transient failure (StrictMode double-mount / axios interceptor race)
    let lastErr = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await api.get(`/admin/clients/${clientId}`);
        setC(data);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // 404 = truly missing → bounce back to list. Any other error → retry once.
        if (status === 404) {
          toast.error("Client not found");
          setLoading(false);
          navigate("/admin/clients");
          return;
        }
        // brief delay before retry
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    setLoading(false);
    toast.error("Could not load client", { description: lastErr?.response?.data?.detail || "" });
  }, [clientId, navigate]);

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get(`/admin/clients/${clientId}/history`); setHistory(data); }
    catch { /* silent */ }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

  const patchField = async (patch) => {
    try { const { data } = await api.patch(`/admin/clients/${clientId}`, patch); setC(data); toast.success("Saved", { duration: 1500 }); }
    catch (err) { toast.error("Save failed", { description: err?.response?.data?.detail || "" }); }
  };

  const addContact = async () => {
    if (!newContact.name.trim()) return;
    const contacts = [...(c.contacts || []), { ...newContact, is_primary: !(c.contacts || []).some((x) => x.is_primary) }];
    await patchField({ contacts });
    setNewContact({ name: "", email: "", phone: "", designation: "" });
  };

  const removeContact = async (idx) => {
    const contacts = (c.contacts || []).filter((_, i) => i !== idx);
    await patchField({ contacts });
  };

  const addAddress = async () => {
    if (!newAddress.line1.trim()) return;
    const addresses = [...(c.addresses || []), newAddress];
    await patchField({ addresses });
    setNewAddress({ label: "Site", line1: "", city: "", country: "IN" });
  };

  const removeAddress = async (idx) => {
    const addresses = (c.addresses || []).filter((_, i) => i !== idx);
    await patchField({ addresses });
  };

  const deleteClient = async () => {
    if (!window.confirm(`Delete ${c.display_name}? This is soft-delete; requires no active projects.`)) return;
    try { await api.delete(`/admin/clients/${clientId}`); toast.success("Client deleted"); navigate("/admin/clients"); }
    catch (err) { toast.error("Delete failed", { description: err?.response?.data?.detail || "" }); }
  };

  if (loading || !c) {
    return <AdminLayout><div className="p-10 text-center text-muted-foreground">Loading client…</div></AdminLayout>;
  }

  const cd = c.company_details || {};

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-client-detail">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <button onClick={() => navigate("/admin/clients")} className="text-sm text-muted-foreground hover:underline flex items-center gap-1 mb-2">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Clients
            </button>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="client-title">{c.display_name}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              <span data-testid="client-number" className="font-mono">{c.display_number}</span>
              {c.manual_override && <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded border border-amber-200">Manual</span>}
              <span>·</span>
              <span className="capitalize flex items-center gap-1">
                {c.client_type === "company" ? <Building2 className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}
                {c.client_type}
              </span>
              <span>·</span>
              <span>{c.country}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="client-delete-btn" variant="ghost" size="icon" onClick={deleteClient} title="Delete client">
              <Trash2 className="h-4 w-4 text-rose-600" />
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="client-tabs">
            <TabsTrigger data-testid="client-tab-overview" value="overview">Overview</TabsTrigger>
            <TabsTrigger data-testid="client-tab-contacts" value="contacts">Contacts {c.contacts?.length ? `(${c.contacts.length})` : ""}</TabsTrigger>
            <TabsTrigger data-testid="client-tab-addresses" value="addresses">Addresses {c.addresses?.length ? `(${c.addresses.length})` : ""}</TabsTrigger>
            <TabsTrigger data-testid="client-tab-history" value="history">History {c._counts ? `(${(c._counts.projects || 0) + (c._counts.enquiries || 0)})` : ""}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Basic details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Display name</Label>
                  <Input data-testid="client-name-input" defaultValue={c.display_name} onBlur={(e) => e.target.value !== c.display_name && patchField({ display_name: e.target.value })} />
                </div>
                <div>
                  <Label>Country</Label>
                  <Select value={c.country} onValueChange={(v) => patchField({ country: v })}>
                    <SelectTrigger data-testid="client-country-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["IN","US","CA","AU","GB","CH"].map((cc) => <SelectItem key={cc} value={cc}>{cc}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Primary email</Label>
                  <Input data-testid="client-email-input" defaultValue={c.primary_email || ""} onBlur={(e) => e.target.value !== c.primary_email && patchField({ primary_email: e.target.value })} />
                </div>
                <div>
                  <Label>Primary phone</Label>
                  <Input data-testid="client-phone-input" defaultValue={c.primary_phone || ""} onBlur={(e) => e.target.value !== c.primary_phone && patchField({ primary_phone: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea data-testid="client-notes-input" rows={3} defaultValue={c.notes || ""} onBlur={(e) => e.target.value !== c.notes && patchField({ notes: e.target.value })} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Company / tax details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ["legal_name","Legal name"], ["trade_name","Trade name"],
                  ["gst_number","GST (IN)"], ["pan_number","PAN (IN)"],
                  ["vat_number","VAT (UK/EU)"], ["ein","EIN (US)"],
                  ["tax_id","Tax ID (generic)"], ["website","Website"],
                ].map(([field, label]) => (
                  <div key={field}>
                    <Label>{label}</Label>
                    <Input
                      data-testid={`client-cd-${field}`}
                      defaultValue={cd[field] || ""}
                      onBlur={(e) => {
                        if (e.target.value !== (cd[field] || "")) {
                          patchField({ company_details: { ...cd, [field]: e.target.value || null } });
                        }
                      }}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Contact persons</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  <Input data-testid="new-contact-name" placeholder="Name*" value={newContact.name} onChange={(e) => setNewContact({...newContact, name: e.target.value})} />
                  <Input data-testid="new-contact-email" placeholder="Email" value={newContact.email} onChange={(e) => setNewContact({...newContact, email: e.target.value})} />
                  <Input data-testid="new-contact-phone" placeholder="Phone" value={newContact.phone} onChange={(e) => setNewContact({...newContact, phone: e.target.value})} />
                  <div className="flex gap-2">
                    <Input data-testid="new-contact-designation" placeholder="Designation" value={newContact.designation} onChange={(e) => setNewContact({...newContact, designation: e.target.value})} />
                    <Button data-testid="add-contact-btn" size="icon" onClick={addContact}><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
                {(c.contacts || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No contacts yet.</div>
                ) : (
                  <ul className="divide-y">
                    {c.contacts.map((ct, i) => (
                      <li key={`${ct.email || ct.name}-${i}`} data-testid={`contact-row-${i}`} className="py-2 flex items-center gap-3">
                        <div className="flex-1">
                          <div className="font-medium">{ct.name} {ct.is_primary && <span className="text-xs text-emerald-700 ml-1">(primary)</span>}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-3">
                            {ct.email && <span><Mail className="h-3 w-3 inline mr-1" />{ct.email}</span>}
                            {ct.phone && <span><Phone className="h-3 w-3 inline mr-1" />{ct.phone}</span>}
                            {ct.designation && <span>· {ct.designation}</span>}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeContact(i)} data-testid={`remove-contact-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="addresses">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" /> Addresses</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                  <Input data-testid="new-address-label" placeholder="Label" value={newAddress.label} onChange={(e) => setNewAddress({...newAddress, label: e.target.value})} />
                  <Input data-testid="new-address-line" placeholder="Line 1*" value={newAddress.line1} onChange={(e) => setNewAddress({...newAddress, line1: e.target.value})} />
                  <Input data-testid="new-address-city" placeholder="City" value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} />
                  <Input data-testid="new-address-country" placeholder="IN" value={newAddress.country} onChange={(e) => setNewAddress({...newAddress, country: e.target.value})} />
                  <Button data-testid="add-address-btn" onClick={addAddress}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                </div>
                {(c.addresses || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No addresses yet.</div>
                ) : (
                  <ul className="divide-y">
                    {c.addresses.map((a, i) => (
                      <li key={`${a.line1 || ""}-${a.city || ""}-${i}`} data-testid={`address-row-${i}`} className="py-2 flex items-start gap-3">
                        <MapPin className="h-4 w-4 mt-1 text-slate-500" />
                        <div className="flex-1">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">{a.label}</div>
                          <div className="text-sm">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                          <div className="text-xs text-muted-foreground">{[a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ")}</div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeAddress(i)} data-testid={`remove-address-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Unified history</CardTitle></CardHeader>
              <CardContent>
                {!history ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">Loading history…</div>
                ) : history.total === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No enquiries, projects, quotes or contracts linked yet.</div>
                ) : (
                  <ul className="divide-y">
                    {history.events.map((e) => {
                      const Icon = KIND_ICONS[e.kind] || Mail;
                      const linkTo = e.kind === "project" ? `/admin/projects/${e.id}`
                                    : e.kind === "enquiry" ? `/admin/enquiries` : "#";
                      return (
                        <li key={`${e.kind}-${e.id}`} data-testid={`history-${e.kind}-${e.id}`} className="py-2 flex items-start gap-3">
                          <Icon className="h-4 w-4 mt-1 text-slate-500" />
                          <div className="flex-1">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">{e.kind} · {e.status || "—"}</div>
                            <Link to={linkTo} className="text-sm font-medium hover:underline">{e.title || "(untitled)"}</Link>
                            <div className="text-xs text-muted-foreground font-mono">{e.reference || ""}</div>
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">{e.created_at?.slice(0, 10)}</div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminClientDetail;
